// components/StatCard.jsx
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import NavigationDots from '../../../components/NavigationDots';

const StatCard = ({ stat, index, isHighlighted }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  const getNumericValue = (value) => {
    const stringValue = value.toString().replace(/[,+%MK]/g, '');
    if (value.toString().includes('M'))
      return parseFloat(stringValue) * 1000000;
    if (value.toString().includes('K')) return parseFloat(stringValue) * 1000;
    return parseFloat(stringValue) || 0;
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${Math.floor(num / 1000)}K`;
    return Math.floor(num).toLocaleString();
  };

  const targetValue = getNumericValue(stat.value);
  const isPercentage = stat.value.toString().includes('%');

  useEffect(() => {
    if (!hasAnimated) return;

    const duration = 1500;
    const steps = 30;
    const increment = targetValue / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= targetValue) {
        setCount(targetValue);
        clearInterval(timer);
      } else {
        setCount(current);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [hasAnimated, targetValue]);

  return (
    <motion.div
      className="stat-item position-relative"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        delay: index * 0.05,
        type: 'spring',
        stiffness: 200,
      }}
      onViewportEnter={() => setHasAnimated(true)}
      viewport={{ once: true }}
      whileHover={{ y: -2 }}
      style={{
        flex: '1 1 auto',
        minWidth: '140px',
        maxWidth: '200px',
      }}
    >
      {/* Glow effect when highlighted */}
      {isHighlighted && (
        <motion.div
          className="position-absolute"
          style={{
            inset: '-2px',
            background:
              'linear-gradient(45deg, transparent, rgba(16, 185, 129, 0.3), transparent)',
            borderRadius: '12px',
            filter: 'blur(8px)',
            zIndex: -1,
          }}
          animate={{
            opacity: [0.5, 1, 0.5],
            rotate: [0, 360],
          }}
          transition={{
            opacity: { duration: 2, repeat: Infinity },
            rotate: { duration: 3, repeat: Infinity, ease: 'linear' },
          }}
        />
      )}

      <div
        className="stat-content d-flex align-items-center gap-3 p-3 position-relative"
        style={{
          background: isHighlighted
            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(16, 185, 129, 0.02))'
            : 'var(--bg-surface, rgba(255, 255, 255, 0.02))',
          border: `1px solid ${
            isHighlighted
              ? 'rgba(16, 185, 129, 0.4)'
              : 'var(--border-color, rgba(255, 255, 255, 0.1))'
          }`,
          borderRadius: '12px',
          backdropFilter: 'blur(10px)',
          transition: 'all 0.3s ease',
          minHeight: '70px',
        }}
      >
        {/* Left side - Animated Icon */}
        <motion.div
          className="stat-icon d-flex align-items-center justify-content-center"
          style={{
            fontSize: '1.5rem',
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: isHighlighted
              ? 'rgba(16, 185, 129, 0.1)'
              : 'var(--bg-surface-darker, rgba(0, 0, 0, 0.05))',
            color: isHighlighted
              ? '#10b981'
              : 'var(--text-secondary, rgba(0, 0, 0, 0.6))',
            filter: isHighlighted
              ? 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.3))'
              : 'none',
            transition: 'all 0.3s ease',
          }}
          animate={
            isHighlighted
              ? {
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0],
                }
              : {}
          }
          transition={{ duration: 0.5 }}
        >
          {stat.icon}
        </motion.div>

        {/* Right side - Value and Label */}
        <div className="flex-grow-1">
          {/* Animated Value */}
          <motion.div
            className="stat-value fw-bold"
            style={{
              fontSize: '1.25rem',
              lineHeight: 1.2,
              color: 'var(--text-primary, #000)',
              textShadow: isHighlighted
                ? '0 0 15px rgba(16, 185, 129, 0.3)'
                : 'none',
              transition: 'text-shadow 0.3s ease',
            }}
          >
            <motion.span
              key={count}
              initial={{ y: -5, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              {formatNumber(count)}
              {isPercentage && '%'}
            </motion.span>
          </motion.div>

          {/* Label */}
          <div
            className="stat-label"
            style={{
              fontSize: '0.7rem',
              color: 'var(--text-secondary, rgba(0, 0, 0, 0.6))',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontWeight: '500',
              marginTop: '2px',
            }}
          >
            {stat.label}
          </div>
        </div>

        {/* Animated underline */}
        <motion.div
          className="position-absolute bottom-0 start-0"
          style={{
            height: '2px',
            background:
              'linear-gradient(90deg, transparent, #10b981, transparent)',
            borderRadius: '0 0 12px 12px',
            left: 0,
            right: 0,
          }}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: isHighlighted ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        />
      </div>
    </motion.div>
  );
};

// Flashy container with smooth animations
const StatsContainer = ({ stats }) => {
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  useEffect(() => {
    // Start highlighting after initial load
    const startDelay = setTimeout(() => {
      setHighlightedIndex(0);
    }, 500);

    return () => clearTimeout(startDelay);
  }, []);

  useEffect(() => {
    if (highlightedIndex === -1) return;

    const interval = setInterval(() => {
      setHighlightedIndex((prev) => (prev + 1) % stats.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [stats.length, highlightedIndex]);

  const handleDotClick = (index) => {
    setHighlightedIndex(index);
  };

  return (
    <div className="stats-wrapper my-5">
      <motion.div
        className="d-flex gap-2 justify-content-center flex-wrap mb-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {stats.map((stat, index) => (
          <StatCard
            key={index}
            stat={stat}
            index={index}
            isHighlighted={highlightedIndex === index}
          />
        ))}
      </motion.div>

      {/* Enhanced navigation dots */}
      <div className="d-flex justify-content-center align-items-center gap-2 mt-4 pt-2">
        <NavigationDots
          totalItems={stats.length}
          activeIndex={highlightedIndex}
          onDotClick={handleDotClick}
          className="mt-4 pt-2"
        />
      </div>
    </div>
  );
};

export { StatCard, StatsContainer };
export default StatCard;
